import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Check, X } from "lucide-react";
import { SectionStepper } from "./SectionStepper";
import { SubsectionPills } from "./SubsectionPills";
import {
  decodeHtml,
  extractSectionsAndSubsections,
  getFormStats,
} from "../data/helper";
import { SignatureModal } from "../components/SignatureModal";
import { useSignatureController, useSignatureFieldHandlers } from "../hooks/useSignature";
import PreviewBanner from "./PreviewBanner";
import NavigationBar from "./NavigationBar";
import {
  TemplateDetails,
  saveForm,
  submitXFAForm,
  publishForm,
  updateFormStatus,
} from "../api/endpoints/fetchTemplates";
import { useMutation } from "@tanstack/react-query";
import { useTemplateFormData } from "../hooks/useTemplateFormData";
import {
  normalizeSectionInputs as normalizeShared,
  ResolveTarget,
  SectionInputs,
  FormValues,
  KeyValuePair,
} from "../lib/normalizeSectionInputs";
import { applyValuesToContainer } from "../lib/applyValues";
import { collectValuesFromContainer } from "../lib/snapshotValues";
import { collectPairsFromContainer } from "../lib/collectPairs";
import { parseFormMetaFromHtml, buildPayloadFromMeta, type ParsedFormMeta } from "../lib/formPayload";
import { useAuth } from "../contexts/AuthContext";
import { SkeletonFormContent } from "./SkeletonPreview";
interface XFAFormWizardPreviewProps {
  template?: TemplateDetails;
  memberId: string;
  htmlId?: string;
  templateId: string;
  onClose?: () => void;
  onSaveSuccess?: (fileId: string) => void;
}

export function XFAFormWizardPreviewNew({
  template,
  templateId,
  htmlId,
  memberId,
  onClose,
  onSaveSuccess,
}: XFAFormWizardPreviewProps) {
  // SIMULATION: Force save success without calling the backend.
  // Set to false to restore real API behavior.
  const SIMULATE_SAVE_SUCCESS = false;
  const templateHtml = template?.htmlContent || "";
  const isPublishedForm = template?.isPublished ?? false;
  const extractedSections = extractSectionsAndSubsections(templateHtml);
  const { user } = useAuth();
  const { data: formDataResponse, isSuccess: formDataSuccess } =
    useTemplateFormData({
      templateId: template?.fileId ?? null, fileName: template?.fileName ?? ""
    });

  const parsedFormData = useMemo(() => {
    try {
      const raw = formDataResponse?.data?.formData;
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as unknown;
      return parsed as TemplateDetails["sectionInputs"];
    } catch {
      return undefined;
    }
  }, [formDataResponse]);

  // Local state for save feedback
  const [showSuccess, setShowSuccess] = useState(false);
  const successTimerRef = useRef<number | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  // Types for section inputs
  // Using shared types
  type PairWithSeq = { key: string; value: string | number | boolean; sequence?: string };
  type SectionPairs = {
    [sectionKey: string]:
    | PairWithSeq[]
    | { [subsectionKey: string]: PairWithSeq[] };
  };

  // Form stats and navigation state
  const formStats = useMemo(
    () => getFormStats(extractedSections),
    [extractedSections]
  );
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentSubsectionIndex, setCurrentSubsectionIndex] = useState(0);

  // Signature controller from lib
  const {
    isOpen: isSignatureModalOpen,
    currentFieldId: signatureFieldId,
    signatureValue,
    setSignatureValue,
    openModal: openSignatureModal,
    closeModal: closeSignatureModal,
    saveSignature,
  } = useSignatureController("#html-section-content");

  // Track signature labels keyed by field id for payload enrichment
  const [signatureLabels, setSignatureLabels] = useState<Record<string, string>>({});

  // Helper to find a human-friendly label associated with a field (by field id attribute)
  const getSignatureLabel = useCallback((fieldId: string): string => {
    const container = document.querySelector('#html-section-content');
    if (!container) return "";
    // Try explicit label[for]
    const forLabel = container.querySelector(`label[for="${CSS.escape(fieldId)}"]`) as HTMLLabelElement | null;
    const normalize = (txt: string) => txt.replace(/\u00A0|&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    if (forLabel && forLabel.textContent) return normalize(forLabel.textContent);
    // Try nearest label within parent
    const inputEl = (container.querySelector(`#${CSS.escape(fieldId)}`)
      || container.querySelector(`[data-id="${CSS.escape(fieldId)}"]`)
      || container.querySelector(`[data-field-id="${CSS.escape(fieldId)}"]`)) as HTMLElement | null;
    if (inputEl) {
      const parentLabel = inputEl.closest('label');
      if (parentLabel && parentLabel.textContent) return normalize(parentLabel.textContent);
      // Try preceding sibling text node or element with role/aria-label
      const prev = inputEl.previousElementSibling as HTMLElement | null;
      if (prev && prev.textContent) return normalize(prev.textContent);
      const aria = inputEl.getAttribute('aria-label');
      if (aria) return normalize(aria);
    }
    // Try display wrapper
    const display = container.querySelector(`[data-signature-field="${CSS.escape(fieldId)}"]`) as HTMLElement | null;
    if (display && display.previousElementSibling && display.previousElementSibling.textContent) {
      return normalize(display.previousElementSibling.textContent);
    }
    return "";
  }, []);

  // Removed: getSignatureMetaByKey (payload builder now uses cached metadata and signatureLabels)

  // Wrap saveSignature to also record the label for payload building (XFA only)
  const saveSignatureWithLabel = useCallback((signature: string) => {
    const container = document.querySelector('#html-section-content');
    const fieldId = signatureFieldId;
    let keyForMap: string | null = null;
    if (container && fieldId) {
      const inputEl = (container.querySelector(`#${CSS.escape(fieldId)}`)
        || container.querySelector(`[data-id="${CSS.escape(fieldId)}"]`)
        || container.querySelector(`[data-field-id="${CSS.escape(fieldId)}"]`)) as HTMLElement | null;
      const dataIdAttr = inputEl?.getAttribute('data-id');
      keyForMap = dataIdAttr || fieldId;
    }
    if (fieldId) {
      const label = getSignatureLabel(fieldId);
      const mapKey = keyForMap || fieldId;
      setSignatureLabels((prev) => ({ ...prev, [mapKey]: label }));
    }
    saveSignature(signature);
  }, [getSignatureLabel, saveSignature, signatureFieldId]);

  // Normalize helper and section inputs state
  // Accepts legacy per-section structure or new array-of-pairs using data-id as key
  // Resolver based on extracted sections
  const resolveTarget: ResolveTarget = (id) => {
    // Find id by scanning current extracted sections HTML
    const extractDataIds = (html?: string): string[] => {
      if (!html) return [];
      const text = decodeHtml ? decodeHtml(html) : html;
      const ids = new Set<string>();
      const re = /data-id\s*=\s*(["'])(.*?)\1/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(text))) {
        if (m[2]) ids.add(m[2]);
      }
      return Array.from(ids);
    };
    for (let si = 0; si < extractedSections.length; si++) {
      const sec = extractedSections[si];
      if (sec.subsections && sec.subsections.length > 0) {
        for (let sj = 0; sj < sec.subsections.length; sj++) {
          const sub = sec.subsections[sj];
          if (extractDataIds(sub.html).includes(id)) {
            return { sectionIndex: si, subsectionIndex: sj };
          }
        }
      } else {
        if (extractDataIds(sec.html).includes(id)) {
          return { sectionIndex: si };
        }
      }
    }
    return null;
  };

  const [sectionInputs, setSectionInputs] = useState<SectionInputs>({});
  const [sectionPairs, setSectionPairs] = useState<SectionPairs>({});

  useEffect(() => {
    if (parsedFormData) {
      setSectionInputs(
        normalizeShared(
          parsedFormData as
          | SectionInputs
          | KeyValuePair[]
          | { [sectionKey: string]: KeyValuePair[] }
          | { [key: string]: { [name: string]: string } },
          resolveTarget
        )
      );

      // Additionally, if parsedFormData is an array (or map) of pairs including sequence,
      // seed sectionPairs so we can restore checkboxes by data-id + data-order.
      const buildPairs = (raw: unknown): SectionPairs => {
        const pairsMap: SectionPairs = {};
        const assign = (key: string, p: PairWithSeq) => {
          const target = resolveTarget(key);
          if (!target) return;
          const sKey = String(target.sectionIndex);
          if (typeof target.subsectionIndex === "number") {
            const subKey = String(target.subsectionIndex);
            const sectionObj = (pairsMap[sKey] as { [subKey: string]: PairWithSeq[] }) || {};
            const arr = (sectionObj[subKey] || []) as PairWithSeq[];
            arr.push({ key: p.key, value: p.value, sequence: p.sequence });
            sectionObj[subKey] = arr;
            pairsMap[sKey] = sectionObj;
          } else {
            const arr = ((pairsMap[sKey] as PairWithSeq[]) || []) as PairWithSeq[];
            arr.push({ key: p.key, value: p.value, sequence: p.sequence });
            pairsMap[sKey] = arr;
          }
        };

        if (Array.isArray(raw)) {
          (raw as Array<PairWithSeq & { key?: string }>).forEach((p) => {
            if (!p?.key) return;
            assign(p.key, { key: p.key, value: p.value ?? "", sequence: p.sequence });
          });
        } else if (raw && typeof raw === "object") {
          Object.values(raw as Record<string, unknown>).forEach((arr) => {
            if (!Array.isArray(arr)) return;
            (arr as Array<PairWithSeq & { key?: string }>).forEach((p) => {
              if (!p?.key) return;
              assign(p.key, { key: p.key, value: p.value ?? "", sequence: p.sequence });
            });
          });
        }
        return pairsMap;
      };

      try {
        const initialPairs = buildPairs(parsedFormData);
        // Only set if we detected any pair arrays in the payload
        if (Object.keys(initialPairs).length > 0) {
          setSectionPairs((prev) => ({ ...prev, ...initialPairs }));
        }
      } catch {
        // ignore initial pairs parsing errors
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedFormData]);

  // Save form mutation
  const { mutate, isPending: mutationIsPending } = useMutation({
    networkMode: "always", // Allow mutation to run even when offline
    mutationFn: async ({
      payload,
      saveClicked,
    }: {
      payload: unknown;
      saveClicked: boolean;
    }) => {
      if (SIMULATE_SAVE_SUCCESS) {
        // Simulate network latency and a successful response
        await new Promise((r) => setTimeout(r, 300));
        return { message: "Simulated success" };
      }
      if (!template?.fileId) {
        throw new Error("Missing fileId for save operation");
      }
      const emailId = user?.email || "";
      if (saveClicked) {
        return saveForm(
          template.fileId,
          payload as Record<string, unknown>,
          emailId,
          memberId
        );
      } else {
        return submitXFAForm(
          template.fileId,
          payload as Record<string, unknown>
        );
      }
    },
    onMutate: () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
      setShowSuccess(false);
      setErrorText(null);
    },
    onSuccess: () => {
      setShowSuccess(true);
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
      successTimerRef.current = window.setTimeout(() => {
        setShowSuccess(false);
        if (template?.fileId && typeof onSaveSuccess === "function") {
          onSaveSuccess(template.fileId);
        }
        if (onClose) onClose();
      }, 2000);
    },
    onError: (err: unknown) => {
      setShowSuccess(false);
      // Try to extract a useful message
      let message = "Failed to save";
      if (typeof err === "object" && err !== null) {
        const maybeWithResponse = err as {
          response?: { data?: { message?: string } };
        };
        const maybeWithMessage = err as { message?: string };
        message =
          maybeWithResponse.response?.data?.message ??
          maybeWithMessage.message ??
          message;
      }
      setErrorText(message);
    },
  });

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
    };
  }, []);

  // Submit form mutation (separate from save)

  const submitFormMutation = useMutation({
    networkMode: "always", // Allow mutation to run even when offline
    mutationFn: async (payload: unknown) => {

      if (!template?.fileId) {
        throw new Error("Missing fileId for submit operation");
      }
      const emailId = user?.email || "";
      // Call saveForm and submitXFAForm in parallel
      const [, submitResult] = await Promise.all([
        saveForm(template.fileId, payload as Record<string, unknown>, emailId, memberId),
        submitXFAForm(
          template.fileId,
          payload as Record<string, unknown>,
          user?.role === "admin"
        ),
      ]);
      return submitResult; // Return the submit result as the primary response
    },
    onMutate: () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
      setShowSuccess(false);
      setErrorText(null);
    },
    onSuccess: async () => {
      // After successful submit, call publish form API

      if (template?.fileId) {
        try {
          const emailId = user?.email || "";
          await updateFormStatus(htmlId, emailId, false);
        } catch (publishErr) {
          console.error("Failed to publish form after submit:", publishErr);
          // Don't block success message even if publish fails
        }
      }

      setShowSuccess(true);
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
      successTimerRef.current = window.setTimeout(() => {
        setShowSuccess(false);
        if (template?.fileId && typeof onSaveSuccess === "function") {
          onSaveSuccess(template.fileId);
        }
        if (onClose) onClose();
      }, 2000);
    },
    onError: (err: unknown) => {
      setShowSuccess(false);
      let message = "Failed to submit";
      if (typeof err === "object" && err !== null) {
        const maybeWithResponse = err as {
          response?: { data?: { message?: string } };
        };
        const maybeWithMessage = err as { message?: string };
        message =
          maybeWithResponse.response?.data?.message ??
          maybeWithMessage.message ??
          message;
      }
      setErrorText(message);
    },
  });

  // Publish form mutation
  const publishFormMutation = useMutation({
    networkMode: "always", // Allow mutation to run even when offline
    mutationFn: async () => {
      if (!template?.fileId) {
        throw new Error("Missing fileId for publish operation");
      }
      const emailId = user?.email || "";
      return publishForm(templateId, emailId, true);
    },
    onMutate: () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
      setShowSuccess(false);
      setErrorText(null);
    },
    onSuccess: () => {
      setShowSuccess(true);
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
      successTimerRef.current = window.setTimeout(() => {
        setShowSuccess(false);
        if (template?.fileId && typeof onSaveSuccess === "function") {
          onSaveSuccess(template.fileId);
        }
        if (onClose) onClose();
      }, 2000);
    },
    onError: (err: unknown) => {
      setShowSuccess(false);
      let message = "Failed to publish";
      if (typeof err === "object" && err !== null) {
        const maybeWithResponse = err as {
          response?: { data?: { message?: string } };
        };
        const maybeWithMessage = err as { message?: string };
        message =
          maybeWithResponse.response?.data?.message ??
          maybeWithMessage.message ??
          message;
      }
      setErrorText(message);
    },
  });

  // Inject minimal CSS for a simple green circle check animation
  useEffect(() => {
    const STYLE_ID = "form-success-anim-style";
    const existing = document.getElementById(
      STYLE_ID
    ) as HTMLStyleElement | null;
    const css = `
      .success-badge { display: inline-flex; align-items: center; gap: 8px; }
      .success-badge__icon {
        width: 20px; height: 20px; border-radius: 9999px;
        background: #10b981; color: #fff;
        display: inline-flex; align-items: center; justify-content: center;
        animation: badge-pop 160ms ease-out 1;
      }
      .error-badge { display: inline-flex; align-items: center; gap: 8px; }
      .error-badge__icon {
        width: 20px; height: 20px; border-radius: 9999px;
        background: #ef4444; color: #fff;
        display: inline-flex; align-items: center; justify-content: center;
        animation: badge-pop 160ms ease-out 1;
      }
      @keyframes badge-pop { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
    `;
    if (existing) {
      existing.innerHTML = css;
    } else {
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.innerHTML = css;
      document.head.appendChild(style);
    }
  }, []);



  // Check if there are no extracted sections - render raw HTML instead
  const hasNoSections = !extractedSections || extractedSections.length === 0;

  // style injection useEffect (PLACE HERE, inside the component function)
  useEffect(() => {
    // Extract <style>...</style> from the chosen HTML
    const styleMatch = templateHtml.match(/<style[^>]*>([\s\S]*?)<\/style>/);
    if (styleMatch) {
      const css = styleMatch[1];
      if (!document.getElementById("xfa-form-style-inject")) {
        const style = document.createElement("style");
        style.id = "xfa-form-style-inject";
        style.innerHTML = css;
        document.head.appendChild(style);
      }
    }
  }, [templateHtml]);

  const section = extractedSections[currentSectionIndex];
  const hasSubsections = section?.subsections && section.subsections.length > 0;
  // Cache form metadata (checkbox sequences + signature labels) from the full HTML
  const [formMeta, setFormMeta] = useState<ParsedFormMeta>({ checkboxIndex: new Map(), signatureKeyToLabel: new Map() });
  useEffect(() => {
    setFormMeta(parseFormMetaFromHtml(templateHtml || ""));
  }, [templateHtml]);
  const subsection = hasSubsections
    ? section.subsections[currentSubsectionIndex]
    : undefined;

  // Snapshot values for section+subsection key
  const getSnapshotKey = useCallback(
    () =>
      hasSubsections
        ? `${currentSectionIndex}_${currentSubsectionIndex}`
        : `${currentSectionIndex}`,
    [hasSubsections, currentSectionIndex, currentSubsectionIndex]
  );

  /**
   * Helper function to save section values in nested structure
   * @param sectionIndex - The section index
   * @param subsectionIndex - The subsection index (undefined if no subsections)
   * @param values - The form values to save
   */
  const saveSectionValues = useCallback(
    (
      sectionIndex: number,
      subsectionIndex: number | undefined,
      values: FormValues
    ) => {
      const sectionKey = String(sectionIndex);

      if (subsectionIndex !== undefined) {
        // Section has subsections - use nested structure
        setSectionInputs((prev) => {
          const subsectionKey = String(subsectionIndex);
          const currentSection = prev[sectionKey];

          // If section doesn't exist or is not nested, create nested structure
          if (
            !currentSection ||
            !(
              typeof currentSection === "object" &&
              !Array.isArray(currentSection)
            )
          ) {
            return {
              ...prev,
              [sectionKey]: {
                [subsectionKey]: values,
              },
            };
          }

          // Check if currentSection is nested (has subsection keys)
          // If it's FormValues (has string values directly), convert to nested
          const isFormValues = Object.values(currentSection).every(
            (v) => typeof v === "string"
          );

          if (isFormValues) {
            // Current section is flat FormValues, convert to nested
            // This shouldn't happen in normal flow, but handle for safety
            return {
              ...prev,
              [sectionKey]: {
                [subsectionKey]: values,
              },
            };
          } else {
            // Already nested structure, update the subsection
            return {
              ...prev,
              [sectionKey]: {
                ...(currentSection as { [subsectionKey: string]: FormValues }),
                [subsectionKey]: values,
              },
            };
          }
        });
      } else {
        // Section has no subsections - store directly
        setSectionInputs((prev) => ({
          ...prev,
          [sectionKey]: values,
        }));
      }
    },
    []
  );

  /**
   * Helper function to retrieve section values from nested structure
   * @param sectionIndex - The section index
   * @param subsectionIndex - The subsection index (undefined if no subsections)
   * @returns The form values for the section/subsection
   */
  const getSectionValues = useCallback(
    (sectionIndex: number, subsectionIndex: number | undefined): FormValues => {
      const sectionKey = String(sectionIndex);
      const sectionData = sectionInputs[sectionKey];

      if (!sectionData) {
        return {};
      }

      if (subsectionIndex !== undefined) {
        // Section has subsections - retrieve from nested structure
        // Check if sectionData is nested (object with subsection keys)
        const isFormValues = Object.values(sectionData).every(
          (v) => typeof v === "string"
        );

        if (isFormValues) {
          // SectionData is flat FormValues, but we expect nested
          // This might happen with old data format, return empty
          return {};
        } else {
          // Nested structure, get the subsection
          const subsectionKey = String(subsectionIndex);
          const nestedSection = sectionData as {
            [subsectionKey: string]: FormValues;
          };
          return nestedSection[subsectionKey] || {};
        }
      } else {
        // Section has no subsections - return directly
        // Check if it's FormValues or nested structure
        const isFormValues = Object.values(sectionData).every(
          (v) => typeof v === "string"
        );

        if (isFormValues) {
          return sectionData as FormValues;
        } else {
          // It's nested but we don't have subsection - return empty
          // This shouldn't happen in normal flow
          return {};
        }
      }
    },
    [sectionInputs]
  );

  /**
   * Helper function to apply prefill values to dynamically injected form fields.
   *
   * This function directly manipulates the DOM because the form HTML is injected
   * via dangerouslySetInnerHTML, which means React doesn't have direct control
   * over these elements. We must use querySelector and direct property assignment
   * instead of React's controlled component pattern.
   *
   * @param sectionKey - The section key (e.g., "0", "1_0") identifying the section
   * @param values - An object mapping field names/ids to their values
   */
  // (Replaced by shared applyValuesToContainer)

  // Ref to track if prefill values have been applied (prevents re-application)
  // This ensures the prefill logic runs only once on first mount, avoiding
  // overwriting user edits if the component re-renders
  const valuesAppliedRef = useRef(false);

  /**
   * Prefill form fields on first mount using data from sectionInputs.
   *
   * This effect runs only once on component mount to populate form fields with
   * initial values from sectionInputs. It does NOT run on re-renders or when
   * sectionInputs changes, to prevent overwriting user edits.
   *
   * Why a timeout?
   * - The HTML is injected via dangerouslySetInnerHTML, which happens asynchronously
   * - We need to wait for the DOM to be fully populated before we can query for elements
   * - A 300ms delay ensures the browser has time to parse and render the injected HTML
   * - This is necessary because React's useEffect runs synchronously after render,
   *   but the DOM manipulation from dangerouslySetInnerHTML may not be complete yet
   *
   * Why direct DOM manipulation instead of React bindings?
   * - dangerouslySetInnerHTML creates DOM nodes that React doesn't track
   * - These elements are not part of React's virtual DOM, so we can't use
   *   controlled components (value/onChange props) or React state
   * - We must use querySelector and direct property assignment to interact with them
   */
  useEffect(() => {
    if (!formDataSuccess) return;
    if (valuesAppliedRef.current) return;
    const container = document.getElementById("html-section-content");
    if (!container) return;

    valuesAppliedRef.current = true;

    setTimeout(() => {
      Object.values(sectionInputs).forEach((sectionData) => {
        const isFormValues =
          sectionData &&
          typeof sectionData === "object" &&
          !Array.isArray(sectionData) &&
          Object.values(sectionData).every((v) => typeof v === "string");

        if (isFormValues) {
          applyValuesToContainer(
            document.getElementById("html-section-content"),
            sectionData as FormValues
          );
        } else {
          const nestedData = sectionData as {
            [subsectionKey: string]: FormValues;
          };
          Object.values(nestedData).forEach((subsectionValues) => {
            applyValuesToContainer(
              document.getElementById("html-section-content"),
              subsectionValues
            );
          });
        }
      });

      // Also apply any checkbox pairs with sequence for current visible section/subsection
      const sectionKey = String(currentSectionIndex);
      let pairsToApply: PairWithSeq[] | undefined;
      const current = sectionPairs[sectionKey];
      if (hasSubsections) {
        const subKey = String(currentSubsectionIndex);
        if (current && !Array.isArray(current)) {
          pairsToApply = (current as { [subKey: string]: PairWithSeq[] })[subKey];
        }
      } else if (current && Array.isArray(current)) {
        pairsToApply = current as PairWithSeq[];
      }
      if (pairsToApply && pairsToApply.length > 0) {
        applyValuesToContainer(
          document.getElementById("html-section-content"),
          {},
          false,
          pairsToApply
        );
      }
    }, 300);
  }, [formDataSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  // Signature modal functions
  // Attach reusable signature field handlers
  useSignatureFieldHandlers("#html-section-content", openSignatureModal, currentSectionIndex, currentSubsectionIndex);

  // Collect current input values from the injected HTML form
  const collectCurrentValues = useCallback(() => {
    const container = document.getElementById("html-section-content");
    return collectValuesFromContainer(container) as FormValues;
  }, []);

  // Collect current inputs strictly using data-id as key
  const collectCurrentKeyValuePairs = useCallback(() => {
    const container = document.getElementById("html-section-content");
    return collectPairsFromContainer(container);
  }, []);

  // Collect pairs including sequence for checkboxes (by data-id + data-order)
  const collectPairsWithSequenceFromContainer = useCallback((): PairWithSeq[] => {
    const container = document.getElementById("html-section-content");
    if (!container) return [];
    const out: PairWithSeq[] = [];
    container.querySelectorAll("input, textarea, select").forEach((el) => {
      const dataId = (el as HTMLElement).getAttribute("data-id");
      if (!dataId) return;
      if (el instanceof HTMLInputElement) {
        if (el.type === "checkbox") {
          const seq = el.getAttribute("data-order");
          out.push({ key: dataId, value: el.checked ? "true" : "false", sequence: seq ?? undefined });
        } else if (el.type === "radio") {
          out.push({ key: dataId, value: el.checked ? "true" : "false" });
        } else if (el.type !== "file") {
          out.push({ key: dataId, value: el.value ?? "" });
        }
      } else if (el instanceof HTMLTextAreaElement) {
        out.push({ key: dataId, value: el.value ?? "" });
      } else if (el instanceof HTMLSelectElement) {
        out.push({ key: dataId, value: el.value ?? "" });
      }
    });
    return out;
  }, []);

  // Flatten all saved section inputs into KeyValuePair[] and normalize values
  const flattenAllSectionInputsToPairs = useCallback((): KeyValuePair[] => {
    const pairs: KeyValuePair[] = [];
    Object.entries(sectionInputs).forEach(([, sectionData]) => {
      if (!sectionData) return;
      const isFormValues =
        typeof sectionData === "object" &&
        !Array.isArray(sectionData) &&
        Object.values(sectionData).every((v) => typeof v === "string");

      if (isFormValues) {
        const values = sectionData as FormValues;
        Object.entries(values).forEach(([key, val]) => {
          pairs.push({ key, value: val ?? "" });
        });
      } else {
        const nested = sectionData as { [subKey: string]: FormValues };
        Object.values(nested).forEach((subValues) => {
          Object.entries(subValues).forEach(([key, val]) => {
            pairs.push({ key, value: val ?? "" });
          });
        });
      }
    });
    return pairs;
  }, [sectionInputs]);

  // Build final payload using cached meta independent of current DOM
  const buildFinalPayload = useCallback((latestByKey: Map<string, string | number | boolean>, allPairs: KeyValuePair[]) => {
    return buildPayloadFromMeta(formMeta, allPairs, latestByKey, signatureLabels);
  }, [formMeta, signatureLabels]);

  const snapshotCurrent = () => {
    const values = collectCurrentValues();
    const pairs = collectPairsWithSequenceFromContainer();

    // Save values using nested structure
    saveSectionValues(
      currentSectionIndex,
      hasSubsections ? currentSubsectionIndex : undefined,
      values
    );

    // Save key/value pairs using nested structure
    const sectionKey = String(currentSectionIndex);
    if (hasSubsections) {
      const subsectionKey = String(currentSubsectionIndex);
      setSectionPairs((prev) => {
        const currentSection = prev[sectionKey];
        if (!currentSection || Array.isArray(currentSection)) {
          return { ...prev, [sectionKey]: { [subsectionKey]: pairs } };
        }
        return {
          ...prev,
          [sectionKey]: {
            ...(currentSection as { [subKey: string]: PairWithSeq[] }),
            [subsectionKey]: pairs,
          },
        };
      });
    } else {
      setSectionPairs((prev) => ({ ...prev, [sectionKey]: pairs }));
    }
  };

  // (removed unused helper)

  // Restore on section/subsection change
  useEffect(() => {
    // Retrieve values using nested structure
    const saved = getSectionValues(
      currentSectionIndex,
      hasSubsections ? currentSubsectionIndex : undefined
    );
    const container = document.getElementById("html-section-content");
    if (!container) return;

    if (container) {
      // Determine sequenced checkbox pairs for this view first
      const sectionKey = String(currentSectionIndex);
      let pairsToApply: PairWithSeq[] | undefined;
      const current = sectionPairs[sectionKey];
      if (hasSubsections) {
        const subKey = String(currentSubsectionIndex);
        if (current && !Array.isArray(current)) {
          pairsToApply = (current as { [subKey: string]: PairWithSeq[] })[subKey];
        }
      } else if (current && Array.isArray(current)) {
        pairsToApply = current as PairWithSeq[];
      }
      const sequencedKeys = new Set<string>((pairsToApply || []).map(p => p.key));

      Object.entries(saved).forEach(([name, value]) => {
        let input:
          | HTMLInputElement
          | HTMLTextAreaElement
          | HTMLSelectElement
          | null = null;
        if (name.includes("::")) {
          const [realName, id] = name.split("::");

          // Prefer finding by ID (most precise)
          input = container.querySelector(`#${CSS.escape(id)}`) as
            | HTMLInputElement
            | HTMLTextAreaElement
            | HTMLSelectElement
            | null;

          // Fallback to name if element has no id
          if (!input) {
            input = container.querySelector(`[name="${realName}"]`) as
              | HTMLInputElement
              | HTMLTextAreaElement
              | HTMLSelectElement
              | null;
          }
        } else {
          // Normal key (no "::"): prefer data-id, fallback to name
          input =
            (container.querySelector(
              `[data-id="${CSS.escape(String(name))}"]`
            ) as
              | HTMLInputElement
              | HTMLTextAreaElement
              | HTMLSelectElement
              | null) ||
            (container.querySelector(`[name="${name}"]`) as
              | HTMLInputElement
              | HTMLTextAreaElement
              | HTMLSelectElement
              | null);
        }

        if (!input) return;

        // 🧠 Handle all possible input types
        if (input instanceof HTMLInputElement) {
          switch (input.type) {
            case "checkbox":
              // If this key participates in sequenced checkbox pairs, skip generic checkbox set
              if (!sequencedKeys.has(name)) {
                input.checked = value === "true";
              }
              break;

            case "radio": {
              const [radioName, radioId] = name.split("::");

              if (radioId) {
                const radio = container.querySelector<HTMLInputElement>(
                  `#${CSS.escape(radioId)}`
                );
                if (radio) {
                  const v = String(value).toLowerCase();
                  const truthy = v === "true" || v === "1" || v === "yes" || v === "on" || v === "y";
                  radio.checked = truthy || String(value) === radio.value;
                }
                break;
              }

              // If we targeted by data-id (name equals data-id), set that specific radio based on truthiness or value match
              const targeted = (container.querySelector(
                `[data-id="${CSS.escape(String(name))}"]`
              ) as HTMLInputElement | null) === input;
              if (targeted) {
                const v = String(value).toLowerCase();
                const truthy = v === "true" || v === "1" || v === "yes" || v === "on" || v === "y";
                (input as HTMLInputElement).checked = truthy || String(value) === (input as HTMLInputElement).value;
                break;
              }

              // Fallback to group by the element's actual name
              const groupName = input.name || radioName;
              if (groupName) {
                const radios = container.querySelectorAll<HTMLInputElement>(
                  `input[type="radio"][name="${CSS.escape(groupName)}"]`
                );
                radios.forEach((radio) => {
                  radio.checked = radio.value === String(value);
                });
              }
              break;
            }

            case "file":
              // cannot programmatically set File input value
              console.warn(`Skipped restoring file input: ${name}`);
              break;

            case "number":
              input.value =
                value !== null && value !== undefined ? String(value) : "";
              break;

            case "date":
            case "datetime-local":
            case "time":
              input.value = String(value);
              break;

            default:
              input.value = String(value ?? "");
          }
        } else if (input instanceof HTMLTextAreaElement) {
          input.value = String(value ?? "");
        } else if (input instanceof HTMLSelectElement) {
          const option = Array.from(input.options).find(
            (opt) => opt.value === String(value)
          );
          if (option) option.selected = true;
        }

        // 🌀 Trigger React / listeners update
        input.dispatchEvent(new Event("input", { bubbles: true }));
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });

      // Also restore checkbox pairs (sequence-aware) for the current section/subsection if present
      if (pairsToApply && pairsToApply.length > 0) {
        applyValuesToContainer(container, {}, false, pairsToApply);
      }
    }
  }, [
    currentSectionIndex,
    currentSubsectionIndex,
    hasSubsections,
    getSectionValues,
    sectionPairs,
  ]);

  // Hide duplicate section titles rendered inside the HTML content
  // e.g., lines like: "Section 1 - ..." or "Section 2 – ..."
  useEffect(() => {
    const container = document.getElementById("html-section-content");
    if (!container) return;

    const candidates = Array.from(
      container.querySelectorAll<HTMLElement>(
        "h1, h2, h3, h4, h5, h6, p, div, span"
      )
    ).slice(0, 10); // Only scan first few nodes near the top

    const pattern = /^Section\s*\d+\s*[-–—]/i;
    for (const el of candidates) {
      const text = (el.textContent || "").trim();
      if (pattern.test(text)) {
        el.remove();
        break;
      }
    }
  }, [currentSectionIndex, currentSubsectionIndex, hasSubsections]);

  // Add event listeners to track real-time input changes
  useEffect(() => {
    const container = document.getElementById("html-section-content");
    if (!container) return;

    const handleInputChange = () => {
      // Input change handler (content validation disabled)
    };

    // Add event listeners for input changes
    container.addEventListener("input", handleInputChange);
    container.addEventListener("change", handleInputChange);

    // Cleanup
    return () => {
      container.removeEventListener("input", handleInputChange);
      container.removeEventListener("change", handleInputChange);
    };
  }, [currentSectionIndex, currentSubsectionIndex, getSnapshotKey]);
  // Inject the exact inline <script> logic from ExtractedForm.html into the global scope once
  const scriptsInjectedRef = useRef(false);

  useEffect(() => {
    if (!templateHtml) return;

    // Wait until the rendered HTML exists in the DOM
    const container = document.getElementById("html-section-content");
    if (!container) return;

    // Avoid double-injection
    if (scriptsInjectedRef.current) return;
    scriptsInjectedRef.current = true;

    try {
      const matches = templateHtml.matchAll(
        /<script[^>]*>([\s\S]*?)<\/script>/gi
      );
      const scriptBlocks = Array.from(matches, (m) => m[1]).filter(Boolean);

      scriptBlocks.forEach((code, index) => {
        const sourceName = `xfa-script-${index}.js`;

        const wrapped = `
        try {
          ${code}
          console.log("XFA script executed: ${sourceName}");
        } catch (err) {
          console.error("XFA script error (${sourceName}):", err);
        }
      `;

        const scriptEl = document.createElement("script");
        scriptEl.type = "text/javascript";
        scriptEl.text = wrapped;

        // The KEY: append script AFTER the form HTML exists
        container.appendChild(scriptEl);
      });

      // IMPORTANT: Trigger DOMContentLoaded AGAIN for scripts that register listeners
      document.dispatchEvent(new Event("DOMContentLoaded"));
    } catch (err) {
      console.error("XFA script injection failed:", err);
    }
  }, [templateHtml, currentSectionIndex, currentSubsectionIndex]);


  // Signature handlers are now managed via the lib hook

  const handleNext = () => {
    snapshotCurrent();

    // Navigate to next subsection or section
    if (
      hasSubsections &&
      currentSubsectionIndex < section.subsections.length - 1
    ) {
      setCurrentSubsectionIndex(currentSubsectionIndex + 1);
    } else if (currentSectionIndex < extractedSections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      setCurrentSubsectionIndex(0);
    }
  };

  const handlePrevious = () => {
    snapshotCurrent();
    if (hasSubsections && currentSubsectionIndex > 0) {
      setCurrentSubsectionIndex(currentSubsectionIndex - 1);
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
      const prevHasSub =
        extractedSections[currentSectionIndex - 1].subsections.length > 0;
      setCurrentSubsectionIndex(
        prevHasSub
          ? extractedSections[currentSectionIndex - 1].subsections.length - 1
          : 0
      );
    }
  };

  // Track Save Changes intent when triggering native form submission
  const saveClickedRef = useRef(false);

  // Separate handler to submit the underlying HTML form
  const submitXfaForm = useCallback(() => {
    const form = document.getElementById("xfaForm") as HTMLFormElement | null;
    if (!form) return;
    // Mark that this submit was triggered by Save Changes
    saveClickedRef.current = true;
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit();
    } else {
      form.dispatchEvent(
        new Event("submit", { bubbles: true, cancelable: true })
      );
    }
  }, []);

  // Handle form submit triggered by submit button inside injected HTML
  const handleSubmit = (
    e: React.FormEvent<HTMLFormElement>,
    saveClicked = false
  ) => {
    e.preventDefault();
    // Prefer explicit arg; fallback to ref set by Save Changes
    const saveIntent = saveClicked || saveClickedRef.current;
    // reset ref after reading
    saveClickedRef.current = false;
    const values = collectCurrentValues();

    // Build a merged snapshot including current inputs
    const sectionKey = String(currentSectionIndex);
    const merged: SectionInputs = { ...sectionInputs };

    if (hasSubsections) {
      const subsectionKey = String(currentSubsectionIndex);
      const currentSection = merged[sectionKey];
      const isFormValues =
        currentSection &&
        typeof currentSection === "object" &&
        !Array.isArray(currentSection) &&
        Object.values(currentSection).every((v) => typeof v === "string");

      if (!currentSection || isFormValues) {
        merged[sectionKey] = { [subsectionKey]: values };
      } else {
        merged[sectionKey] = {
          ...(currentSection as { [subsectionKey: string]: FormValues }),
          [subsectionKey]: values,
        };
      }
    } else {
      merged[sectionKey] = values;
    }

    // Update state (async) and log the merged snapshot immediately
    setSectionInputs(merged);
    console.log("Submitted sectionInputs:", merged);

    // Build final payload from all saved pairs and values across sections
    const fromSectionPairs: KeyValuePair[] = [];
    Object.values(sectionPairs).forEach((sectionData) => {
      if (Array.isArray(sectionData)) {
        fromSectionPairs.push(...sectionData);
      } else {
        Object.values(sectionData).forEach((subPairs) => {
          fromSectionPairs.push(...subPairs);
        });
      }
    });

    // Also include flattened values from sectionInputs (covers sections never snapshot)
    const fromSectionInputs = flattenAllSectionInputsToPairs();

    // Include current DOM pairs to ensure latest edits are captured for active section
    const currentPairs = collectCurrentKeyValuePairs();

    // Merge with precedence: currentPairs > fromSectionPairs > fromSectionInputs
    const latestByKey = new Map<string, string | number | boolean>();
    fromSectionInputs.forEach((p) => latestByKey.set(p.key, p.value));
    fromSectionPairs.forEach((p) => latestByKey.set(p.key, p.value));
    currentPairs.forEach((p) => latestByKey.set(p.key, p.value));

    // Build final payload using shared helper
    const allPairs = [...fromSectionPairs, ...currentPairs];
    const finalPayload = buildFinalPayload(latestByKey, allPairs);
    mutate({ payload: finalPayload, saveClicked: saveIntent });
  };

  const isFirst =
    currentSectionIndex === 0 &&
    (!hasSubsections || currentSubsectionIndex === 0);
  const isLast =
    currentSectionIndex === extractedSections.length - 1 &&
    (!hasSubsections ||
      currentSubsectionIndex === section.subsections.length - 1);

  // Enable Submit Form button only on last section
  const isSubmitEnabled = useMemo(() => {
    const onLastSection = currentSectionIndex === extractedSections.length - 1;
    if (!hasSubsections) {
      return onLastSection;
    }
    const onLastSubsection =
      currentSubsectionIndex === section.subsections.length - 1;
    return onLastSection && onLastSubsection;
  }, [
    currentSectionIndex,
    extractedSections.length,
    hasSubsections,
    currentSubsectionIndex,
    section,
  ]);

  // Handle Submit Form button click
  const handleSubmitForm = useCallback(() => {
    // Collect current values and build final payload
    const values = collectCurrentValues();
    const sectionKey = String(currentSectionIndex);
    const merged: SectionInputs = { ...sectionInputs };

    if (hasSubsections) {
      const subsectionKey = String(currentSubsectionIndex);
      const currentSection = merged[sectionKey];
      const isFormValues =
        currentSection &&
        typeof currentSection === "object" &&
        !Array.isArray(currentSection) &&
        Object.values(currentSection).every((v) => typeof v === "string");

      if (!currentSection || isFormValues) {
        merged[sectionKey] = { [subsectionKey]: values };
      } else {
        merged[sectionKey] = {
          ...(currentSection as { [subsectionKey: string]: FormValues }),
          [subsectionKey]: values,
        };
      }
    } else {
      merged[sectionKey] = values;
    }

    // Build final payload from all saved pairs and values
    const fromSectionPairs: KeyValuePair[] = [];
    Object.values(sectionPairs).forEach((sectionData) => {
      if (Array.isArray(sectionData)) {
        fromSectionPairs.push(...sectionData);
      } else {
        Object.values(sectionData).forEach((subPairs) => {
          fromSectionPairs.push(...subPairs);
        });
      }
    });

    const fromSectionInputs = flattenAllSectionInputsToPairs();
    const currentPairs = collectCurrentKeyValuePairs();

    const latestByKey = new Map<string, string | number | boolean>();
    fromSectionInputs.forEach((p) => latestByKey.set(p.key, p.value));
    fromSectionPairs.forEach((p) => latestByKey.set(p.key, p.value));
    currentPairs.forEach((p) => latestByKey.set(p.key, p.value));

    // Build final payload using shared helper
    const allPairs = [...fromSectionPairs, ...currentPairs];
    const finalPayload = buildFinalPayload(latestByKey, allPairs);
    console.log(finalPayload);
    submitFormMutation.mutate(finalPayload);
  }, [collectCurrentValues, currentSectionIndex, sectionInputs, hasSubsections, currentSubsectionIndex, sectionPairs, flattenAllSectionInputsToPairs, collectCurrentKeyValuePairs, submitFormMutation, buildFinalPayload]);

  // Handle Publish Form button click
  const handlePublishForm = useCallback(() => {
    publishFormMutation.mutate();
  }, [publishFormMutation]);

  // Check if next button should be disabled due to no content in current subsection or section
  const isNextDisabled = useMemo(() => {
    // Always enable Next button (disabled functionality removed)
    return isLast;
  }, [isLast]);

  // handleFieldChange intentionally omitted; not used here

  {
    /* Always-visible Submit Row below the pills area (even if no subsections) */
  }
  const hasMemberId = memberId !== null && memberId !== undefined;
  const isAdmin = user?.role === "admin";
  const isSaveDisabled = !isAdmin && !hasMemberId;
  const submitRow = () => {
    return (
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-end gap-3">
          {errorText && (
            <span className="error-badge" aria-live="assertive">
              <span className="error-badge__icon" aria-hidden="true">
                <X className="w-3 h-3" />
              </span>
              <span className="text-sm text-red-600 truncate max-w-[200px]">
                {errorText}
              </span>
            </span>
          )}
          {showSuccess && (
            <span className="success-badge" aria-live="polite">
              <span className="success-badge__icon" aria-hidden="true">
                <Check className="w-3 h-3" />
              </span>
              <span className="text-sm text-green-600">Saved</span>
            </span>
          )}
          {isPublishedForm && (
            <>
              <button
                type="button"
                onClick={submitXfaForm}
                disabled={
                  isSaveDisabled ||
                  mutationIsPending ||
                  submitFormMutation.isPending ||
                  publishFormMutation.isPending
                }
                className={`px-4 py-1.5 text-sm rounded-full transition ${mutationIsPending ||
                  submitFormMutation.isPending ||
                  publishFormMutation.isPending
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "btn-secondary"
                  }`}
              >
                {mutationIsPending ? "Saving..." : "Save Changes"}
              </button>
              {isSubmitEnabled && (
                <button
                  type="button"
                  onClick={handleSubmitForm}
                  disabled={
                    isSaveDisabled ||
                    mutationIsPending ||
                    submitFormMutation.isPending ||
                    publishFormMutation.isPending
                  }
                  className={`px-4 py-1.5 text-sm rounded-full transition ${mutationIsPending ||
                    submitFormMutation.isPending ||
                    publishFormMutation.isPending
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "btn-primary"
                    }`}
                >
                  {submitFormMutation.isPending
                    ? "Submitting..."
                    : "Submit Form"}
                </button>
              )}
            </>
          )}
          {!isPublishedForm && (
            <button
              type="button"
              onClick={handlePublishForm}
              disabled={
                mutationIsPending ||
                submitFormMutation.isPending ||
                publishFormMutation.isPending
              }
              className={`px-4 py-1.5 text-sm rounded-full transition ${mutationIsPending ||
                submitFormMutation.isPending ||
                publishFormMutation.isPending
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "btn-primary"
                }`}
            >
              {publishFormMutation.isPending ? "Publishing..." : "Publish Form"}
            </button>
          )}
        </div>
      </div>
    );
  };

  // If there are no extracted sections, render raw HTML directly
  if (hasNoSections) {
    return (
      <div className="preview-with-no-subsection flex flex-col h-full bg-gray-50">
        {submitRow()}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            <div
              className="preview-form-content"
              dangerouslySetInnerHTML={{
                __html: templateHtml,
              }}
            />
          </div>
        </div>
        <PreviewBanner
          note="Preview Mode - Displaying raw form HTML (no sections detected)"
          sectionCount={formStats.sectionCount}
          subsectionCount={formStats.subsectionCount}
          fieldCount={formStats.totalFields}
        />
      </div>
    );
  }



  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500">Progress</span>
          <span className="text-xs text-primary">
            {Math.round(
              ((currentSectionIndex + 1) / extractedSections.length) * 100
            )}
            %
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{
              width: `${((currentSectionIndex + 1) / extractedSections.length) * 100
                }%`,
            }}
          />
        </div>
      </div>

      {/* Section Stepper */}
      <SectionStepper
        sections={extractedSections.map((s) => ({ id: s.id, name: s.name }))}
        currentIndex={currentSectionIndex}
      // onSelect={(idx) => {
      //   if (idx === currentSectionIndex) return;
      //   snapshotCurrent();
      //   setCurrentSectionIndex(idx);
      //   setCurrentSubsectionIndex(0);
      // }}
      />

      {/* Subsection Pills */}
      {hasSubsections && (
        <SubsectionPills
          subsections={section.subsections.map((s) => ({
            id: s.id,
            name: s.name,
          }))}
          currentIndex={currentSubsectionIndex}
        // onSelect={(idx) => {
        //   if (idx === currentSubsectionIndex) return;
        //   snapshotCurrent();
        //   setCurrentSubsectionIndex(idx);
        // }}
        />
      )}

      {submitRow()}

      {/* Section/Subsection Content Display */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {formDataSuccess ? (
          <div className="max-w-6xl mx-auto bg-white border border-gray-200 rounded-lg p-4">
            <form id="xfaForm" onSubmit={handleSubmit}>
              <div
                id="html-section-content"
                className="prose max-w-none preview-form-content"
                dangerouslySetInnerHTML={{
                  __html: decodeHtml(
                    hasSubsections && subsection
                      ? subsection.html
                      : section.html
                  ),
                }}
              />
            </form>
          </div>
        ) : (
          <SkeletonFormContent />
        )}
      </div>

      {/* Navigation */}
      <NavigationBar
        isFirst={isFirst}
        isLast={isLast}
        isNextDisabled={isNextDisabled}
        currentLabel={`Section ${currentSectionIndex + 1}${hasSubsections ? `.${currentSubsectionIndex + 1}` : ""
          } of ${extractedSections.length}`}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />

      {/* Preview Banner */}
      <PreviewBanner
        note="Preview Mode - This is how the form will appear to users"
        sectionCount={formStats.sectionCount}
        subsectionCount={formStats.subsectionCount}
        fieldCount={formStats.totalFields}
      />

      {/* Signature Modal */}
      <SignatureModal
        isOpen={isSignatureModalOpen}
        value={signatureValue}
        onClose={closeSignatureModal}
        onChange={setSignatureValue}
        onSave={saveSignatureWithLabel}
      />
    </div>
  );
}
